import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinRoom } from '../services/gameService';
import { createFriendlyGame } from '../services/gameModesService';
import { FaUser, FaGamepad, FaArrowRight, FaUsers, FaInfoCircle } from 'react-icons/fa';
import logo from '../assets/logo.png';
import '../App.css';

const Home = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gameMode, setGameMode] = useState('join'); // 'join', 'friendly'
  const [roomId, setRoomId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [friendlySettings, setFriendlySettings] = useState({
    abilities: true,
    gridSize: 8,
    shipCount: 'default'
  });

  // Generate player ID
  const generatePlayerId = () => Math.random().toString(36).substring(2, 8).toUpperCase();

  const handleJoinRoom = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!roomId.trim()) {
        throw new Error('Please enter a room code');
      }

      if (!playerName.trim()) {
        throw new Error('Please enter your name');
      }

      const playerId = generatePlayerId();
      const formattedRoomId = roomId.trim().toUpperCase();

      // Join the room
      const joinSuccess = await joinRoom(formattedRoomId, playerId, playerName.trim());

      if (!joinSuccess) {
        throw new Error('Failed to join room');
      }

      // Store player info
      localStorage.setItem('battleshipPlayerId', playerId);
      localStorage.setItem('battleshipPlayerName', playerName.trim());
      localStorage.setItem('battleshipRoomId', formattedRoomId);

      // Navigate to ship placement page
      navigate(`/place-ships/${formattedRoomId}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFriendlyGame = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!playerName.trim()) {
        throw new Error('Please enter your name');
      }

      const playerId = generatePlayerId();

      // Create friendly game
      const roomId = await createFriendlyGame(playerId, playerName.trim(), friendlySettings);

      // Store player info
      localStorage.setItem('battleshipPlayerId', playerId);
      localStorage.setItem('battleshipPlayerName', playerName.trim());
      localStorage.setItem('battleshipRoomId', roomId);

      // Join the created room
      await joinRoom(roomId, playerId, playerName.trim());

      // Navigate to ship placement page
      navigate(`/place-ships/${roomId}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderGameModeContent = () => {
    switch (gameMode) {
      case 'friendly':
        return (
          <div className="flex flex-col h-full">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400 uppercase tracking-wider">
                Tactical Lobby
              </h3>
              <p className="text-xs text-emerald-500/60 font-mono tracking-widest mt-1">
                // INITIALIZE PRIVATE SECTOR
              </p>
            </div>

            <div className="space-y-6 flex-1">
              <div className="group">
                <label className="text-xs font-mono text-emerald-500/60 ml-1 mb-1 block">OPERATOR NAME</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                    <FaUser className="text-emerald-500/40 group-focus-within:text-emerald-400 transition-colors" />
                  </div>
                  <input
                    type="text"
                    placeholder="ENTER CALLSIGN"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    className="w-full py-4 pl-12 pr-4 bg-emerald-950/20 text-emerald-100 rounded-sm border-b-2 border-emerald-500/20 focus:border-emerald-500 focus:bg-emerald-950/40 outline-none transition-all placeholder:text-emerald-700/50 font-mono"
                  />
                </div>
              </div>

              {/* Friendly Game Settings */}
              <div className="bg-emerald-950/20 p-4 border border-emerald-500/10 hover:border-emerald-500/30 transition-all">
                <label className="flex items-center justify-between text-emerald-100 text-sm cursor-pointer group">
                  <span className="font-mono text-emerald-400/80 group-hover:text-emerald-300">SPECIAL OPS [ABILITIES]</span>
                  <input
                    type="checkbox"
                    checked={friendlySettings.abilities}
                    onChange={(e) => setFriendlySettings({
                      ...friendlySettings,
                      abilities: e.target.checked
                    })}
                    className="w-5 h-5 accent-emerald-500 bg-gray-900 border-emerald-500 rounded cursor-pointer hover:accent-emerald-400 focus:ring-2 focus:ring-emerald-500/50"
                  />
                </label>
              </div>
            </div>

            <button
              onClick={handleCreateFriendlyGame}
              disabled={loading || !playerName}
              className="group relative w-full overflow-hidden rounded-sm bg-emerald-600 p-px shadow-[0_0_20px_rgba(5,150,105,0.4)] transition-all hover:shadow-[0_0_30px_rgba(5,150,105,0.6)] disabled:opacity-50 mt-auto"
            >
              <div className="relative flex items-center justify-center gap-3 bg-gray-900 py-4 px-8 transition-all group-hover:bg-gray-900/0">
                <span className="font-black tracking-widest text-emerald-500 group-hover:text-white transition-colors">
                  {loading ? 'INITIALIZING...' : 'ESTABLISH LINK'}
                </span>
                <FaUsers className="text-emerald-500 group-hover:text-white transition-colors" />
              </div>
            </button>
          </div>
        );

      default: // 'join'
        return (
          <div className="flex flex-col h-full">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 uppercase tracking-wider">
                Mission Command
              </h3>
              <p className="text-xs text-blue-500/60 font-mono tracking-widest mt-1">
                // JOIN EXISTING OPERATION
              </p>
            </div>

            <div className="space-y-6 flex-1">
              <div className="group">
                <label className="text-xs font-mono text-blue-500/60 ml-1 mb-1 block">OPERATOR NAME</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                    <FaUser className="text-blue-500/40 group-focus-within:text-blue-400 transition-colors" />
                  </div>
                  <input
                    type="text"
                    placeholder="ENTER CALLSIGN"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    className="w-full py-4 pl-12 pr-4 bg-blue-950/20 text-blue-100 rounded-sm border-b-2 border-blue-500/20 focus:border-blue-500 focus:bg-blue-950/40 outline-none transition-all placeholder:text-blue-700/50 font-mono"
                  />
                </div>
              </div>

              <div className="group">
                <label className="text-xs font-mono text-blue-500/60 ml-1 mb-1 block">TARGET COORDINATES</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                    <FaGamepad className="text-blue-500/40 group-focus-within:text-blue-400 transition-colors" />
                  </div>
                  <input
                    type="text"
                    placeholder="ROOM CODE"
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                    className="w-full py-4 pl-12 pr-4 bg-blue-950/20 text-blue-100 rounded-sm border-b-2 border-blue-500/20 focus:border-blue-500 focus:bg-blue-950/40 outline-none transition-all placeholder:text-blue-700/50 uppercase tracking-[0.2em] font-mono"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleJoinRoom}
              disabled={loading || !roomId || !playerName}
              className="group relative w-full overflow-hidden rounded-sm bg-blue-600 p-px shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.6)] disabled:opacity-50 mt-auto"
            >
              <div className="relative flex items-center justify-center gap-3 bg-gray-900 py-4 px-8 transition-all group-hover:bg-gray-900/0">
                <span className="font-black tracking-widest text-blue-500 group-hover:text-white transition-colors">
                  {loading ? 'CONNECTING...' : 'ENGAGE OBJECTIVE'}
                </span>
                <FaArrowRight className="text-blue-500 group-hover:text-white transition-colors" />
              </div>
            </button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950/95 relative overflow-hidden font-sans">
      {/* Animated Background Layers - No Stars */}
      <div className="grid-background"></div>

      {/* Radial Glow Overlay for depth */}
      <div className="absolute inset-0 bg-radial-gradient from-blue-900/10 via-transparent to-transparent pointer-events-none"></div>

      {/* Main Container - Responsive: Full width/height on mobile, 3:4 on larger screens */}
      {/* max-w-[400px] keeps it phone-sized on desktop. sm:aspect-[3/4] enforces ratio only on non-mobile. */}
      {/* h-[90vh] ensures it fits on mobile screens without scrolling if possible */}
      <div className="relative z-10 w-full max-w-lg sm:max-w-[550px] h-screen sm:h-auto sm:aspect-[3/4] p-4 sm:p-6 flex flex-col items-center justify-center transition-all duration-300">

        {/* Holographic Form Card */}
        <div className="w-full h-full bg-gray-900/80 backdrop-blur-xl border border-white/10 relative overflow-hidden flex flex-col shadow-2xl rounded-lg sm:rounded-none">
          {/* Decorative Corner Accents */}
          <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-cyan-500/50"></div>
          <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-cyan-500/50"></div>
          <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-cyan-500/50"></div>
          <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-cyan-500/50"></div>

          {/* Scanline Effect */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDI1Ii8+Cjwvc3ZnPg==')] pointer-events-none z-0"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent animate-scan pointer-events-none z-0"></div>

          {/* Card Header (Logo Area) */}
          <div className="relative z-10 flex-none h-32 sm:h-40 flex items-center justify-center bg-gradient-to-b from-black/40 to-transparent pt-4">
            <div className="relative group">
              <div className="absolute -inset-4 bg-cyan-500/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <img
                src={logo}
                alt="Battleship"
                className="h-20 sm:h-24 w-auto object-contain drop-shadow-[0_0_10px_rgba(6,182,212,0.5)] transition-transform duration-300 transform group-hover:scale-110"
              />
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-6 p-2 bg-red-500/10 border-l-2 border-red-500 text-red-400 text-xs font-mono animate-pulse">
              [ERROR]: {error}
            </div>
          )}

          {/* Navigation Tabs */}
          <div className="flex border-y border-white/5 mx-6 mb-4">
            <button
              onClick={() => setGameMode('join')}
              className={`flex-1 py-3 text-xs font-black tracking-widest transition-all relative ${gameMode === 'join'
                ? 'text-cyan-400 bg-cyan-950/30'
                : 'text-gray-500 hover:text-cyan-200 hover:bg-white/5'
                }`}
            >
              JOIN
              {gameMode === 'join' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]"></div>}
            </button>
            <button
              onClick={() => setGameMode('friendly')}
              className={`flex-1 py-3 text-xs font-black tracking-widest transition-all relative ${gameMode === 'friendly'
                ? 'text-emerald-400 bg-emerald-950/30'
                : 'text-gray-500 hover:text-emerald-200 hover:bg-white/5'
                }`}
            >
              CREATE
              {gameMode === 'friendly' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]"></div>}
            </button>
          </div>

          {/* Main Content Area - Flexible height */}
          <div className="flex-1 px-6 pb-6 relative z-10 overflow-y-auto custom-scrollbar">
            {renderGameModeContent()}
          </div>

          {/* Footer Status Line */}
          <div className="h-8 border-t border-white/10 bg-black/40 flex items-center justify-between px-6">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-[10px] text-gray-500 font-mono">SYSTEM ONLINE</span>
            </div>
            <button
              onClick={() => navigate('/game-guide')}
              className="text-[10px] text-cyan-500/60 hover:text-cyan-400 font-mono tracking-wider flex items-center gap-1 transition-colors"
            >
              <FaInfoCircle className="text-[9px]" /> MANUAL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
